/**
 * 端到端加密锁徽章（14.14b；仅私聊，ChatPage 标题区挂载，AfterViewControl
 * 火焰徽章同构）。两个弹层（密钥输入 / 关闭强确认）21af 起**拆成懒块**
 * EncryptionDialogs——徽章常驻首屏，弹层只有点过才出现。
 *
 * - 徽章：开启=闭合锁（绿），关闭=开口锁（灰）。旧版入口在好友详情页（14.21），
 *   新版标题区恒显作为唯一入口，与火焰徽章同一刻意偏差。
 * - 关闭态点击 → 密钥输入（移动=底部 sheet / 桌面=锚定浮层，afterView 同构）：
 *   明文输入经 chat/encryption/set 在 Worker 内 SHA-256 派生落账号库 kv，
 *   明文不出 Worker 不落盘（14.14a）。零 HTTP。
 * - 开启态点击 → 关闭强确认：密钥从本机删除、已有密文不可解（旧版同口径）。
 * - set 回包更新 timeline.encryptionOn 切片；恰是打开中会话 Worker 推 invalidate
 *   重读解密视图（开=旧密文解开、关=「需要密钥」占位），本组件无需刷新逻辑。
 */
import { useCallback, useRef, useState } from 'preact/hooks';
import { messagesConnection } from '~/store/appWorker';
import { EncryptionDialogsLazy } from './EncryptionDialogsLazy';
import type { EncryptionView } from './EncryptionDialogs';
import { IconLock } from '~/ui/icons';
import styles from './encryption.module.scss';

export function EncryptionControl({
  on,
  mobile,
  onError,
}: {
  /** 当前会话加密态（timeline.encryptionOn 切片，set 回包更新） */
  on: boolean;
  mobile: boolean;
  /** 设置未受理（Worker 异常窗口）：调用方 toast */
  onError: () => void;
}) {
  const [view, setView] = useState<EncryptionView | null>(null);
  const [keyInput, setKeyInput] = useState('');
  const badgeRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setView(null), []);

  const toggle = (): void => {
    if (view !== null) {
      setView(null);
      return;
    }
    if (on) {
      setView({ kind: 'confirm' });
      return;
    }
    setKeyInput('');
    const rect = badgeRef.current?.getBoundingClientRect();
    setView(
      rect !== undefined
        ? { kind: 'key', top: rect.bottom + 4, right: window.innerWidth - rect.right }
        : { kind: 'key', top: 52, right: 8 },
    );
  };

  const apply = (input: string | null): void => {
    setView(null);
    void messagesConnection()
      .setEncryptionKey(input)
      .then((ok) => {
        if (!ok) onError();
      });
  };

  return (
    <>
      <button
        ref={badgeRef}
        type="button"
        className={on ? `${styles.badge} ${styles.badgeOn}` : styles.badge}
        data-testid="enc-badge"
        data-active={on || undefined}
        aria-label="端到端加密"
        title="端到端加密"
        onClick={toggle}
      >
        <IconLock class={styles.badgeIcon} open={!on} />
      </button>
      {view !== null && (
        <EncryptionDialogsLazy
          view={view}
          mobile={mobile}
          keyInput={keyInput}
          onKeyInput={setKeyInput}
          onClose={close}
          onSubmit={() => {
            const value = keyInput.trim();
            if (value !== '') apply(value);
          }}
          onConfirmClose={() => apply(null)}
        />
      )}
    </>
  );
}
